import React from 'react';
import moment from 'moment';
import { ApLoaderDefault } from '@alixpartners/ui-components';
import {
	ApIcon,
	ApButtonMain,
	ApButtonGroup,
} from '@alixpartners/ui-components';

import { chartOrange } from 'styles/colors';
import PageToolbar from 'components/PageToolbar';
import { MessageWithAction } from 'components/MessageWithAction';
import { PERMISSIONS } from 'contexts/PermissionsContext';
import {
	getTaxonomyReleaseNotes,
	upgradeTaxonomy,
	getSpendCubeSettings,
} from 'utils/apiRequests';

import {
	Wrapper,
	StyledNavLink as NavLink,
	ChangesBlock,
	ChangesBlockTitle,
	ChangesList,
	ChangesListHeader,
	ChangesListRow,
	ChangesListCell,
	ActiveList,
	ActiveRow,
	ActiveRowCell,
	ActiveRowCellTitle,
	ActiveRowCellValue,
	ActiveListDescription,
	NotificationWrapper,
	ModalBody,
	StyledModal,
	StyledButtonSecondary,
	ModalHeader,
	PreviousUpgradesSection,
	SectionTitle,
	PreviousUpgradesSectionTitle,
	AppliedDateInfoText,
	LoaderWrapper,
	ChangesNoItemsMessage,
} from './styles';

const CHANGE_TYPES = {
	remove: 'remove',
	new: 'new',
	rename: 'rename',
	move: 'move',
	merge: 'merge',
	split: 'split',

	merged: 'merged',
	moved: 'moved',
	removed: 'removed',
	renamed: 'renamed',
};

const CONFIGS = {
	[CHANGE_TYPES.remove]: {
		description:
			'This category has been removed. All previous spend for this category has been assigned to the default category below.',
		headers: [
			'New hierarchy position',
			'Default category',
			'Reason for removal',
		],
	},
	[CHANGE_TYPES.new]: {
		description:
			'A new category has been created. You may start to see spend associated with this category from your next data ingestion.',
		headers: ['Category description', 'Reason for creation'],
	},
	[CHANGE_TYPES.rename]: {
		description: 'This category has been renamed.',
		headers: ['Previous name', 'Reason for rename'],
	},
	[CHANGE_TYPES.move]: {
		description:
			'This category has been moved into a new position. All previous and future spend for this category has been moved from the position below.',
		headers: ['Previous hierarchy position', 'Reason for move'],
	},
	[CHANGE_TYPES.merge]: {
		description:
			'The following categories have been merged. All future spend will be assigned to the default category.',
		headers: [
			'Categories merged',
			'Default category for future spend',
			'Reason for merge',
		],
	},
	[CHANGE_TYPES.split]: {
		description:
			'The above category has been split into new categories. All previous spend for the above category can be found in the default category; new spend will be assigned to the appropriate category..',
		headers: [
			'Categories created',
			'Default category for future spend',
			'Reason for merge',
		],
	},
};
CONFIGS.removed = CONFIGS.remove;
CONFIGS.renamed = CONFIGS.rename;
CONFIGS.moved = CONFIGS.move;
CONFIGS.merged = CONFIGS.merge;

const NOTIFICATION_TITLE =
	'A new version of the Standard Taxonomy is ready to be applied';
const NOTIFICATION_TEXT =
	'Periodically we update our Standard Taxonomy, to improve the categorisation of spend items. Your next data ingestion will be categorised against the latest version once these updates have been applied. Please note that these updates may change the categorisation of some of your historical spend items. In addition, any associated opportunities or categorisation override rules may be adjusted. You can view the taxonomy changes below.';
const SUCCESS_NOTIFICATION_TITLE =
	'A new version of the Standard Taxonomy has been successfully applied';
const SUCCESS_NOTIFICATION_TEXT = 'You can view details of the changes below';

export class TaxonomyViewer extends React.Component {
	state = {
		updatesData: [],
		isConfirmationOpen: false,
		isLoading: true,
		showApplySuccessMessage: false,
	};

	componentDidMount() {
		this.fetchData();
	}

	showConfirmation = () => {
		this.setState({ isConfirmationOpen: true });
	};

	hideConfirmation = () => {
		this.setState({ isConfirmationOpen: false });
	};

	preprocessUpdatesData = updatesData => {
		return updatesData.map(update => ({
			...update,
			releaseNotes:
				update.releaseNotes &&
				update.releaseNotes.map(releaseNote => {
					if (releaseNote.type.toLowerCase() === CHANGE_TYPES.merged) {
						const [
							mergedCategories,
							targetCategories,
							...restDetails
						] = releaseNote.details;

						if (
							mergedCategories &&
							targetCategories &&
							targetCategories[0] &&
							mergedCategories.indexOf(targetCategories[0]) === -1
						) {
							const updatedReleaseNote = {
								...releaseNote,
								details: [
									[...mergedCategories, targetCategories[0]],
									targetCategories,
									...restDetails,
								],
							};

							return updatedReleaseNote;
						}
					}
					return releaseNote;
				}),
		}));
	};

	fetchData = async () => {
		const [updatesData, settingsData] = await Promise.all([
			getTaxonomyReleaseNotes(),
			getSpendCubeSettings(),
		]);

		this.setState({
			updatesData: this.preprocessUpdatesData(updatesData),
			settingsData,
			isLoading: false,
		});
	};

	upgradeTaxonomy = async () => {
		this.setState({
			isApplying: true,
		});

		await upgradeTaxonomy();

		const settingsData = await getSpendCubeSettings();

		this.setState({
			isApplying: false,
			settingsData,
			showApplySuccessMessage: true,
		});
	};

	render() {
		const { permissions } = this.props;
		const {
			isConfirmationOpen,
			isLoading,
			updatesData,
			settingsData,
			isApplying,
			showApplySuccessMessage,
		} = this.state;

		if (isLoading) {
			return (
				<LoaderWrapper>
					<ApLoaderDefault />
				</LoaderWrapper>
			);
		}

		if (!updatesData || !updatesData.length) {
			return <div>No taxonomy version updates</div>;
		}

		const canUpgradeTaxonomyVersion = permissions.includes(
			PERMISSIONS.TaxonomyApplyNew
		);

		const currentChanges = updatesData[0];
		const appliedChanges = updatesData.slice(1) || [];
		const isCurrentVersionAppied =
			showApplySuccessMessage ||
			updatesData[0].version === settingsData.taxonomyVersion;

		return (
			<React.Fragment>
				<PageToolbar>
					<NavLink exact to="/taxonomyviewer">
						Taxonomy Viewer
					</NavLink>
				</PageToolbar>
				<Wrapper>
					{currentChanges && (
						<React.Fragment>
							{isCurrentVersionAppied ? (
								<React.Fragment>
									{showApplySuccessMessage && (
										<NotificationWrapper isSuccess>
											<MessageWithAction
												title={SUCCESS_NOTIFICATION_TITLE}
												content={SUCCESS_NOTIFICATION_TEXT}
												isSuccess
											/>
										</NotificationWrapper>
									)}
									<SectionTitle>
										Current taxonomy version
										{settingsData.taxonomyVersionAppliedDate && (
											<AppliedDateInfoText>
												{`Applied on ${moment(
													settingsData.taxonomyVersionAppliedDate
												).format('D MMM YYYY, HH:mm')}`}
											</AppliedDateInfoText>
										)}
									</SectionTitle>
								</React.Fragment>
							) : canUpgradeTaxonomyVersion ? (
								isApplying ? (
									<LoaderWrapper>
										<ApLoaderDefault />
									</LoaderWrapper>
								) : (
									<NotificationWrapper>
										<MessageWithAction
											title={NOTIFICATION_TITLE}
											content={NOTIFICATION_TEXT}
											button={{
												text: 'Apply new taxonomy',
												onClick: this.showConfirmation,
												style: { width: 194 },
											}}
										/>
									</NotificationWrapper>
								)
							) : (
								<SectionTitle>New taxonomy version available</SectionTitle>
							)}
							<TaxonomyChangesTable
								data={currentChanges.releaseNotes}
								title={`Changes in Version ` + currentChanges.version}
								open
							/>
						</React.Fragment>
					)}

					<PreviousUpgradesSection
						isSuccess={showApplySuccessMessage || isCurrentVersionAppied}
					>
						<PreviousUpgradesSectionTitle>
							Previous upgrades
						</PreviousUpgradesSectionTitle>
						{appliedChanges.map((change, i) => (
							<TaxonomyChangesTable
								key={i}
								data={change.releaseNotes}
								title={`Taxonomy v` + change.version}
							/>
						))}
					</PreviousUpgradesSection>
				</Wrapper>
				<StyledModal
					isOpen={isConfirmationOpen}
					hasClosed={this.hideConfirmation}
					header={<ModalHeader>Upgrade Standard Taxonomy version</ModalHeader>}
					footer={({ handleClose }) => {
						return (
							<ApButtonGroup justifyContent="flex-end">
								<StyledButtonSecondary onClick={handleClose}>
									Cancel
								</StyledButtonSecondary>
								<ApButtonMain
									onClick={() => {
										this.upgradeTaxonomy();
										handleClose();
									}}
								>
									Confirm
								</ApButtonMain>
							</ApButtonGroup>
						);
					}}
				>
					<ModalBody>
						<p>
							You are about to upgrade the Standard Taxonomy to the latest
							version.
						</p>
						<br />
						<p>
							This may change the categorisation of some of your historical
							spend items, may affect any associated classification rules you
							have created and will also remove any project taxonomy mappings
							made using the indirect taxonomy mapper.
						</p>
					</ModalBody>
				</StyledModal>
			</React.Fragment>
		);
	}
}

class TaxonomyChangesTable extends React.Component {
	state = { open: !!this.props.open, activeRowIndex: null };

	toggleOpen = () => {
		this.setState({ open: !this.state.open });
	};

	setActiveRowIndex = activeRowIndex => {
		if (this.state.activeRowIndex === activeRowIndex) {
			this.setState({ activeRowIndex: null });
			return;
		}

		this.setState({ activeRowIndex });
	};

	renderItem(item, headerIndex) {
		if (headerIndex === 0 && item.type.toLowerCase() === CHANGE_TYPES.merged) {
			return (
				<ul>
					{item.details[headerIndex].map((detail, i) => (
						<li key={detail + i}>{detail}</li>
					))}
				</ul>
			);
		}

		if (
			headerIndex === 1 &&
			item.details[1] &&
			item.details[1].length === 0 &&
			CONFIGS[item.type.toLowerCase()].headers.length === 2 &&
			item.details[2].length > 0
		) {
			headerIndex = 2;
		}

		return item.details[headerIndex].join(' > ');
	}

	render() {
		const { open, activeRowIndex } = this.state;
		const { data, title } = this.props;

		return (
			<ChangesBlock>
				<ChangesBlockTitle onClick={this.toggleOpen} open={open}>
					{title}
				</ChangesBlockTitle>
				{open &&
					(!data || !data.length ? (
						<ChangesNoItemsMessage>
							<ApIcon
								iconName="outline_warning"
								iconSize="24px"
								iconColor={chartOrange}
							/>
							Changes in this version of the taxonomy are for maintenance
							purposes only and will not affect the categorisation of your
							spend.
						</ChangesNoItemsMessage>
					) : (
						<ChangesList>
							<ChangesListHeader>
								<ChangesListCell>Change type</ChangesListCell>
								<ChangesListCell>Category</ChangesListCell>
								<ChangesListCell>New hierarchy</ChangesListCell>
							</ChangesListHeader>
							{data.map((item, idx) => (
								<React.Fragment key={idx}>
									<ChangesListRow
										isActive={idx === activeRowIndex}
										onClick={() => this.setActiveRowIndex(idx)}
									>
										<ChangesListCell>{item.type}</ChangesListCell>
										<ChangesListCell>{item.category}</ChangesListCell>
										<ChangesListCell>
											{item.newHierarchy ? item.newHierarchy.join(' > ') : '-'}
										</ChangesListCell>
									</ChangesListRow>
									{idx === activeRowIndex &&
										(CONFIGS[item.type.toLowerCase()] ? (
											<ActiveList>
												<ActiveListDescription>
													{CONFIGS[item.type.toLowerCase()].description}
												</ActiveListDescription>
												<ActiveRow>
													{CONFIGS[item.type.toLowerCase()].headers.map(
														(headerItem, headerIndex) => (
															<ActiveRowCell key={headerIndex}>
																<ActiveRowCellTitle>
																	{headerItem}
																</ActiveRowCellTitle>
																<ActiveRowCellValue>
																	{this.renderItem(item, headerIndex)}
																</ActiveRowCellValue>
															</ActiveRowCell>
														)
													)}
												</ActiveRow>
											</ActiveList>
										) : (
											<ActiveList>
												<ActiveListDescription>{`Unsupported type "${
													item.type
												}"`}</ActiveListDescription>
											</ActiveList>
										))}
								</React.Fragment>
							))}
						</ChangesList>
					))}
			</ChangesBlock>
		);
	}
}

export default TaxonomyViewer;
